import {Button, Divider} from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AddIcon from '@material-ui/icons/Add';
import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {NavLink, useHistory} from 'react-router-dom';
import {MerchantContext, SecurityContext} from '../../../App';
import {ReactComponent as PersonIcon} from '../../../assets/images/person-icon.svg';
import {ReactComponent as StoreIcon} from '../../../assets/images/shop-icon.svg';
import {ReactComponent as Logo} from '../../../assets/images/swiftpay-logo.svg';
import {ReactComponent as LogoutIcon} from '../../../assets/images/logout-icon.svg';
import {ReactComponent as LockIcon} from '../../../assets/images/lock-icon.svg';
import {RoutesPaths} from '../../../routes/routes.paths';
import {Merchant} from '../../../shared/model/Merchant.model';
import {removeSessionToken} from '../../../utils/security';
import {TransHelper} from '../../../utils/trans-helper';
import styles from './Navbar.module.scss';
import MainLogo from '../mainLogo/MainLogo';
import {EnvironmentType} from '../../../shared/model/EnvironmentType.model';

export const PrefixTrans = TransHelper.getPrefixedTrans('NAVBAR');

export default function Navbar(): ReactElement {

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
  const open = Boolean(anchorEl);
  const history = useHistory();
  const [blockShopChange, setBlockShopChange] = useState(false);

  const [anchorMerchants, setAnchorMerchants] = useState<null | HTMLElement>();
  const openMerchants = Boolean(anchorMerchants);

  const {merchants, currentMerchant, env, setCurrentMerchant} = useContext(MerchantContext);
  const {isLogged, setIsLogged} = useContext(SecurityContext);

  const isEnabledDisbursement = currentMerchant?.enableDisbursements || env === EnvironmentType.SANDBOX;

  const handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMerchantsSwitch = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorMerchants(event.currentTarget);
  };

  useEffect(() => {
    const disabledForRoutes = [RoutesPaths.SHOP_NEW, RoutesPaths.CHANGE_PASSWORD];
    for (let index = 0; index < disabledForRoutes.length; index++) {
      const route = disabledForRoutes[index];
      if (route === history.location.pathname) {
        setBlockShopChange(true);
        break;
      }
    }
  }, [history.location]);

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleMerchantClose = (): void => {
    setAnchorMerchants(null);
  };

  const handleMerchantSwitch = (merchant: Merchant): void => {
    handleMerchantClose();
    setCurrentMerchant(merchant);
  };

  const handleLogout = (): void => {
    removeSessionToken();
    setIsLogged(false);
    handleClose();
    history.replace('/login');
  };

  return (
    <AppBar position='static' color={'transparent'} className={styles.navbar}>
      <div className={styles.navBarContent}>
        <div className={styles.logoLinkContainer}>
          <Link href={RoutesPaths.ROOT} className={styles.logoLink}>
            <MainLogo className={styles.customLogo}>
              <Logo className={styles.logo} title='SwiftPay' />
            </MainLogo>
          </Link>
        </div>
        {
          isLogged && (
            <>
              <div className={styles.links}>
                <Link component={NavLink}
                  to={RoutesPaths.ROOT}
                  exact
                  className={styles.navLink}
                  activeClassName={styles.activeLink}>
                  <PrefixTrans>PAYMENTS</PrefixTrans>
                </Link>
                <Link component={NavLink}
                  to={RoutesPaths.PAY_BY_LINK}
                  exact
                  className={styles.navLink}
                  activeClassName={styles.activeLink}>
                  <PrefixTrans>PAY_BY_LINK</PrefixTrans>
                </Link>
                {/* <Link component={NavLink}
                  to={RoutesPaths.SETTLEMENTS}
                  exact
                  className={styles.navLink}
                  activeClassName={styles.activeLink}>
                  <PrefixTrans>SETTLEMENTS</PrefixTrans>
                </Link> */}
                {isEnabledDisbursement && (
                  <Link component={NavLink}
                    to={RoutesPaths.DISBURSEMENTS}
                    exact
                    className={styles.navLink}
                    activeClassName={styles.activeLink}>
                    <PrefixTrans>DISBURSEMENTS</PrefixTrans>
                  </Link>
                )}
                <Link component={NavLink}
                  to={RoutesPaths.SETTINGS}
                  exact
                  className={styles.navLink}
                  activeClassName={styles.activeLink}>
                  <PrefixTrans>SETTINGS</PrefixTrans>
                </Link>
              </div>
              <div className={styles.toolbar}>
                <IconButton aria-label='account of current user'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  onClick={handleMenu}
                  color='inherit'
                  className={styles.dropdownIconButtons}>
                  <PersonIcon className={styles.personIcon} />
                </IconButton>
                <Menu id='menu-appbar'
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  open={open}
                  onClose={handleClose}>
                  <MenuItem onClick={(): void => history.push(RoutesPaths.CHANGE_PASSWORD)} className={styles.menuItem}>
                    <LockIcon className={styles.menuItemIcon} />
                    <PrefixTrans>CHANGE_PASSWORD</PrefixTrans>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout} className={styles.menuItem}>
                    <LogoutIcon className={styles.menuItemIcon} />
                    <PrefixTrans>LOGOUT</PrefixTrans>
                  </MenuItem>
                </Menu>
                {merchants && merchants.length > 0 && currentMerchant
                  ? <>
                    <Button className={styles.merchantsButton}
                      aria-label='Merchants'
                      aria-controls='menu-merchants'
                      aria-haspopup='true'
                      onClick={handleMerchantsSwitch}
                      color='inherit'
                      variant='outlined'
                      disabled={blockShopChange}>
                      <StoreIcon className={styles.merchantsButtonIcon} />
                      <small className={styles.menuItem}>{currentMerchant?.name}</small>
                    </Button>
                    <Menu id='menu-merchants'
                      anchorEl={anchorMerchants}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      onClose={handleMerchantClose}
                      open={openMerchants}>
                      {
                        merchants?.map((merchant): ReactElement => (
                          <MenuItem onClick={(): void => handleMerchantSwitch(merchant)}
                            key={merchant.id}
                            selected={merchant.id === currentMerchant?.id}
                            className={styles.menuItem}>
                            <StoreIcon className={styles.menuItemIcon} />
                            {merchant.name}
                          </MenuItem>
                        ))
                      }
                      <Divider />
                      <MenuItem className={styles.menuItem} onClick={(): void => history.push(RoutesPaths.SHOP_NEW)}>
                        <AddIcon className={styles.menuItemIcon} />
                        <PrefixTrans>NEW_SHOP</PrefixTrans>
                      </MenuItem>
                    </Menu>
                  </>
                  : ('')
                }
              </div>
            </>
          )}
      </div>
    </AppBar>
  );
}
